/**
 * WeaveIn.ai — Disaster Case Management dashboard API service.
 *
 * TRUS-538. Backs the DCM section of the Reports page: caseload tiles, the
 * status / household-type / disaster / unmet-need breakdowns, per-case-manager
 * load, the flat case rollup behind the detail grid, and the org-wide team
 * activity feed.
 *
 * Uses the absolute production API base like the other services — never a
 * relative /api path, which Vercel proxies to the dev backend.
 */

import axios, { AxiosInstance } from 'axios';

import { config as productConfig } from '@/src/config';

// ============================================================================
// TYPES
// ============================================================================

/** One aging threshold and how many open cases have passed it. */
export interface AgingBucket {
  days: number;
  count: number;
}

export interface DcmTotals {
  total_cases: number;
  active_cases: number;
  active_households: number;
  active_members: number;
  /**
   * Cases with no linked household. This is why the household-type chart can be
   * mostly "Unknown" — it is surfaced rather than folded away.
   */
  cases_without_household: number;
  new_cases_this_week: number;
  new_cases_this_month: number;
  closed_this_week: number;
  closed_this_month: number;
  closed_ytd: number;
  avg_days_open: number | null;
  max_days_open: number | null;
  open_over_days: AgingBucket[];
  unassigned_open_cases: number;
}

/** One slice of a breakdown axis. `key` is the raw value, already trimmed. */
export interface DcmBreakdownRow {
  key: string;
  total_count: number;
  open_count: number;
  closed_count: number;
  avg_days_open: number | null;
}

export interface DcmCaseloadRow {
  user_id: number | null;
  name: string;
  total_cases: number;
  open_cases: number;
  closed_cases: number;
  avg_days_open: number | null;
  max_days_open: number | null;
  /** Open cases past the first aging threshold. */
  stale_cases: number;
}

/**
 * Transfer counts are an approximation over an audit trail that records the new
 * assignee but not the previous one — `caveats` says so in the server's own
 * words, and the UI renders it rather than presenting the numbers bare.
 */
export interface DcmTransfers {
  total_transfers: number;
  transferred_in: number;
  transferred_out: number;
  unknown_origin: number;
  cases_transferred: number;
  source: string;
  caveats: string[];
}

/** Where each axis came from, so an unconfigured chart reads as unconfigured. */
export interface DcmSources {
  case_grain: string;
  case_status: 'custom_field' | 'enrollment_status';
  household_type: string;
  primary_need: string;
}

export interface DcmOverview {
  generated_at: string;
  filters: Record<string, unknown>;
  totals: DcmTotals;
  breakdowns: {
    by_status: DcmBreakdownRow[];
    by_household_type: DcmBreakdownRow[];
    by_disaster: DcmBreakdownRow[];
    by_primary_need: DcmBreakdownRow[];
  };
  by_dcm: DcmCaseloadRow[];
  transfers: DcmTransfers;
  sources: DcmSources;
}

export interface DcmCaseRow {
  case_id: number;
  member_id: number;
  first_name: string | null;
  last_name: string | null;
  household_number: string | null;
  program_name: string | null;
  disaster: string;
  case_status: string;
  enrollment_status: string;
  household_type: string;
  primary_need: string;
  assigned_to_name: string | null;
  opened_date: string | null;
  closed_date: string | null;
  days_open: number | null;
}

export interface DcmCasesPage {
  columns: string[];
  total: number;
  limit: number;
  offset: number;
  rows: DcmCaseRow[];
}

// ---------------------------------------------------------------------------
// Team activity feed (phase 2)
// ---------------------------------------------------------------------------

/** What a feed row is. `note` and `task` are acts; the rest are system records. */
export type DcmFeedKind = 'note' | 'task' | 'member_update' | 'case_update' | 'workflow' | 'activity';
export type DcmFeedOrigin = 'note' | 'system';

export interface DcmActivityItem {
  /** Namespaced ("note:12") — ids are not unique across the merged sources. */
  id: string;
  kind: DcmFeedKind;
  origin: DcmFeedOrigin;
  occurred_at: string | null;
  title: string | null;
  body: string | null;
  /** Who did it. The per-member feed calls this `author`; org-wide it is broader. */
  actor_id: number | null;
  actor_name: string | null;
  /** Who it was about — the thing a per-member feed never needs to say. */
  member_id: number | null;
  member_name: string | null;
  meta: Record<string, any>;
}

export interface DcmActivityPage {
  items: DcmActivityItem[];
  window_days: number;
  since: string;
  /** What this window pulled, NOT a total across every source. Page on has_more. */
  merged_count: number;
  limit: number;
  offset: number;
  has_more: boolean;
}

export interface DcmActivityFilters {
  days?: number;
  actor_id?: number;
  member_id?: number;
  origin?: DcmFeedOrigin;
}

export interface DcmFilters {
  program_id?: number;
  assigned_to?: number;
  opened_from?: string;
  opened_to?: string;
  open_only?: boolean;
}

// ============================================================================
// CLIENT
// ============================================================================

const BASE = '/api/reports/dcm';

class DcmDashboardAPI {
  private client: AxiosInstance;

  constructor(baseURL: string = (productConfig.weaveinApi.baseUrl ?? '').trim()) {
    this.client = axios.create({
      baseURL,
      headers: { 'Content-Type': 'application/json' },
    });

    this.client.interceptors.request.use((config) => {
      const token = localStorage.getItem('auth_token');
      const cboId = localStorage.getItem('active_cbo_id');
      if (token) config.headers.Authorization = `Bearer ${token}`;
      // Without X-CBO-ID a multi-org user reads another org's caseload.
      if (cboId) config.headers['X-CBO-ID'] = cboId;
      return config;
    });
  }

  /** Strip undefined so an unset filter is absent rather than "undefined". */
  private params(filters: DcmFilters | DcmActivityFilters, extra: Record<string, unknown> = {}) {
    return Object.fromEntries(
      Object.entries({ ...filters, ...extra }).filter(([, v]) => v !== undefined && v !== null && v !== ''),
    );
  }

  async getOverview(filters: DcmFilters = {}): Promise<DcmOverview> {
    const res = await this.client.get(`${BASE}/overview`, { params: this.params(filters) });
    return res.data;
  }

  async listCases(filters: DcmFilters = {}, limit = 100, offset = 0): Promise<DcmCasesPage> {
    const res = await this.client.get(`${BASE}/cases`, { params: this.params(filters, { limit, offset }) });
    return res.data;
  }

  async getActivity(filters: DcmActivityFilters = {}, limit = 50, offset = 0): Promise<DcmActivityPage> {
    const res = await this.client.get(`${BASE}/activity`, {
      params: this.params(filters, { limit, offset }),
    });
    return res.data;
  }

  /**
   * The whole filtered rollup as a CSV download.
   *
   * Server-rendered rather than built from the loaded page, so the file matches
   * the filters instead of whichever page happened to be on screen — and so it
   * stays byte-identical to the scheduled email attachment once that ships.
   */
  async downloadCasesCsv(filters: DcmFilters = {}): Promise<void> {
    const res = await this.client.get(`${BASE}/cases.csv`, {
      params: this.params(filters),
      responseType: 'blob',
    });
    const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv;charset=utf-8;' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `dcm-cases-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }
}

export const dcmDashboardAPI = new DcmDashboardAPI();
export default dcmDashboardAPI;
